<template>
  <!--
    Модальне вікно для перегляду PDF-файлів.
    Браузерний тулбар прихований (#toolbar=0), зверху власний хедер із назвою та діями.
  -->
  <teleport to="body">
    <div
      class="pdf-modal"
      tabindex="-1"
      role="dialog"
      aria-hidden="false"
    >
      <!-- Підкладка для затемнення фону -->
      <div class="pdf-modal__overlay" @click="this.$emit('close')"></div>

      <div class="pdf-modal__container">
        <!-- Власний хедер -->
        <div class="pdf-modal__header">
          <div class="pdf-modal__title" :title="name">
            <i class="ri-file-pdf-2-line"></i>
            <span>{{ name }}</span>
          </div>

          <div class="pdf-modal__actions">
            <!-- Попередній файл -->
            <button
              v-if="ifPrevopen != undefined"
              class="pdf-modal__btn"
              :disabled="!ifPrevopen"
              :title="$t('back')"
              @click="this.$emit('prevOpen')"
            >
              <i class="ri-arrow-left-s-line"></i>
            </button>

            <!-- Наступний файл -->
            <button
              v-if="ifNextopen != undefined"
              class="pdf-modal__btn"
              :disabled="!ifNextopen"
              :title="$t('Next')"
              @click="this.$emit('nextOpen')"
            >
              <i class="ri-arrow-right-s-line"></i>
            </button>

            <!-- Завантажити -->
            <button
              class="pdf-modal__btn"
              :title="$t('Download')"
              @click="this.$emit('download')"
            >
              <i class="ri-download-cloud-2-line"></i>
            </button>

            <!-- Видалити -->
            <button
              class="pdf-modal__btn pdf-modal__btn--danger"
              :title="$t('Delete')"
              @click="this.$emit('remove')"
            >
              <i class="ri-delete-bin-line"></i>
            </button>

            <!-- Закрити -->
            <button
              class="pdf-modal__btn pdf-modal__btn--close"
              :title="$t('close')"
              @click="this.$emit('close')"
            >
              <i class="ri-close-line"></i>
            </button>
          </div>
        </div>

        <!-- Тіло: сам PDF -->
        <div class="pdf-modal__body">
          <iframe
            :src="frameSrc"
            :title="name"
            class="pdf-modal__frame"
          ></iframe>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { storeS } from "@/store"; // глобальне сховище даних

export default {
  // src - blob/URL PDF; name - назва файлу для хедера
  // ifPrevopen / ifNextopen - чи активні стрілки навігації між файлами
  props: ["src", "name", "ifPrevopen", "ifNextopen"],
  created() {
    // Додавання класу модального вікна в масив відкритих модалок
    storeS.modalsOpen.push("pdf");

    // Обробник клавіші "Esc" (зберігаємо посилання, щоб зняти при демонтажі)
    this.escHandler = () => {
      if (
        storeS.modalsOpen[storeS.modalsOpen.length - 1] == "pdf" &&
        !document.querySelector("#preloader")
      ) {
        this.$emit("close");
      }
    };
    this.eventKey.on("esc", this.escHandler);
  },
  unmounted() {
    // Прибирання за собою: знімаємо esc-лістенер та свій запис у modalsOpen
    if (this.escHandler) {
      this.eventKey.off("esc", this.escHandler);
    }
    var idx = storeS.modalsOpen.lastIndexOf("pdf");
    if (idx !== -1) {
      storeS.modalsOpen.splice(idx, 1);
    }
  },
  computed: {
    frameSrc() {
      // Ховаємо вбудований тулбар браузера, щоб показати власний хедер
      if (!this.src) {
        return this.src;
      }
      return `${this.src}#toolbar=0&navpanes=0`;
    },
  },
};
</script>

<style scoped>
.pdf-modal {
  position: fixed;
  inset: 0;
  z-index: 1056;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-modal__overlay {
  position: absolute;
  inset: 0;
  background: #2e2e2eb8;
  backdrop-filter: blur(5px);
}

.pdf-modal__container {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: 85vw;
  height: 92vh;
  background: var(--vz-card-bg, #fff);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
}

.pdf-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--vz-border-color, #e9ebec);
  background: var(--vz-card-bg, #fff);
  flex-shrink: 0;
}

.pdf-modal__title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-weight: 600;
  font-size: 15px;
  color: var(--vz-body-color, #212529);
}

.pdf-modal__title i {
  font-size: 20px;
  color: #f06548;
  flex-shrink: 0;
}

.pdf-modal__title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pdf-modal__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.pdf-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--vz-body-color, #495057);
  font-size: 18px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.pdf-modal__btn:hover:not(:disabled) {
  background: var(--vz-light, #f3f6f9);
}

.pdf-modal__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pdf-modal__btn--danger:hover:not(:disabled) {
  background: rgba(240, 101, 72, 0.12);
  color: #f06548;
}

.pdf-modal__btn--close:hover:not(:disabled) {
  background: rgba(240, 101, 72, 0.12);
  color: #f06548;
}

.pdf-modal__body {
  flex: 1;
  min-height: 0;
  background: #525659;
}

.pdf-modal__frame {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

@media (max-width: 768px) {
  .pdf-modal__container {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
  }

  .pdf-modal__title {
    font-size: 14px;
  }
}
</style>
